import { Heart, Users, Target, Clock, Award, Globe, Shield, BookOpen, ChevronRight } from 'lucide-react';
import ProgramDetail from '@/components/ProgramDetail';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Peace = () => {
  const { t } = useLanguage();
  
  const peaceDetails = [
    t('programs.peace.details.background', 'VJN, as an organization working with youth, recognized the importance of teaching and training youth about peace culture through various activities. Following a workshop with GIZ in October 2009, we established this program to promote lasting peace in Rwanda and the Great Lakes region.'),
    t('programs.peace.details.role', 'The program contributes to promoting lasting peace in Rwanda, strengthening youth capacity for peaceful conflict resolution, and building conviviality networks among youth from the Great Lakes region.'),
    t('programs.peace.details.scope', 'While our primary scope of work is in Rwanda, we participate in regional, African, and global activities through partnerships and invitations from our partners.'),
    t('programs.peace.details.activities', 'Our activities include training, dialogue, debate, public speaking, theatre, film education (CINEDUC), peace camps, visits, sports for peace, arts for peace, festivals, conferences, summits, and research.')
  ];

  const objectives = [
    t('programs.peace.objectives.main', 'Resolve injustice in nonviolent ways, transform and manage the structural conditions that lead to conflicts.'),
    t('programs.peace.objectives.specific1', 'Strengthen youth capacities for non-violent conflict resolution in communities and the Great Lakes region.'),
    t('programs.peace.objectives.specific2', 'Promote peace, unity, and reconciliation through diversity acceptance, tolerance, and trauma healing.'),
    t('programs.peace.objectives.specific3', 'Contribute to sustainable peace in cross-border regions by creating safe spaces for dialogue.'),
    t('programs.peace.objectives.specific4', 'Create spaces for youth to learn, exchange ideas, and develop skills for peaceful living.')
  ];

  const targetGroups = {
    direct: [
      t('programs.peace.target.direct.youth', 'Youth aged 18-30'),
      t('programs.peace.target.direct.animators', 'Eclaireurs Animateurs and Co-Eclaireurs Animateurs'),
      t('programs.peace.target.direct.refugees', 'Youth in refugee camps'),
      t('programs.peace.target.direct.parents', 'Parents'),
      t('programs.peace.target.direct.students', 'Youth in secondary schools and universities'),
      t('programs.peace.target.direct.outOfSchool', 'Youth out of schools'),
      t('programs.peace.target.direct.clubs', 'Clubs in schools and communities'),
      t('programs.peace.target.direct.initiatives', 'Youth peace initiatives'),
      t('programs.peace.target.direct.communities', 'People in host communities'),
      t('programs.peace.target.direct.prisoners', 'Prisoners and their families')
    ],
    indirect: [
      t('programs.peace.target.indirect.authorities', 'Authorities'),
      t('programs.peace.target.indirect.partners', 'National and international partners'),
      t('programs.peace.target.indirect.community', 'Community members')
    ]
  };

  const selectionCriteria = [
    t('programs.peace.criteria.age', 'Youth aged between 18-30'),
    t('programs.peace.criteria.initiative', 'Have a youth initiative'),
    t('programs.peace.criteria.literacy', 'Skills in reading and writing'),
    t('programs.peace.criteria.club', 'Be a member of a peace club at school'),
    t('programs.peace.criteria.language', 'At least know English or French (especially for peace Fellows)')
  ];

  const approaches = [
    {
      title: t('programs.peace.approaches.diplomacy.title', 'Preventive Diplomacy'),
      description: t('programs.peace.approaches.diplomacy.description', 'Proactive efforts to prevent violence or conflict before escalation through diplomatic dialogue, mediation, and confidence-building measures.'),
      icon: <Globe className="h-6 w-6" />
    },
    {
      title: t('programs.peace.approaches.resolution.title', 'Conflict Resolution and Mediation'),
      description: t('programs.peace.approaches.resolution.description', 'Finding mutually acceptable solutions through third-party mediation, negotiation, and creating platforms for direct communication.'),
      icon: <Shield className="h-6 w-6" />
    },
    {
      title: t('programs.peace.approaches.reconciliation.title', 'Reconciliation'),
      description: t('programs.peace.approaches.reconciliation.description', 'Repairing relationships through truth commissions, transitional justice, community dialogues, and cultural exchanges.'),
      icon: <Heart className="h-6 w-6" />
    },
    {
      title: t('programs.peace.approaches.education.title', 'Peace Education and Advocacy'),
      description: t('programs.peace.approaches.education.description', 'Instilling values of nonviolence and tolerance through school programs, public campaigns, and media projects.'),
      icon: <BookOpen className="h-6 w-6" />
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Hero Section with Background Image */}
      <section className="relative mb-16 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/90 z-10" />
        <div className="relative z-20 p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block p-3 bg-blue-600/30 rounded-full mb-6">
              <Heart className="h-8 w-8" />
            </div>
            <h1 className="text-5xl font-bold mb-6">
              {t('programs.peace.title', 'Peace Building Program')}
            </h1>
            <p className="text-xl mb-8 text-blue-100 leading-relaxed">
              {t('programs.peace.intro.description', 'Our Peace Building Program addresses the root causes of conflict by equipping youth with tools for conflict resolution and promoting a culture of peace. Through training, dialogue, and capacity-building initiatives, we empower young people to become agents of change in their communities.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Users className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">1000+</div>
                  <div className="text-sm text-blue-100">Participants</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Globe className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">5+</div>
                  <div className="text-sm text-blue-100">Regions</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Heart className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">20+</div>
                  <div className="text-sm text-blue-100">Peace Initiatives</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="md:w-1/3">
            <div className="bg-white p-8 rounded-xl shadow-md border border-blue-100">
              <div className="text-blue-600 mb-6">
                <Heart size={48} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {t('programs.peace.title', 'Peace Building Program')}
              </h2>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">
                {t('programs.peace.subtitle', 'Building Peace Through Youth Empowerment')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t('programs.peace.description', 'Our program addresses the root causes of conflict by equipping youth with tools for conflict resolution and promoting a culture of peace in Rwanda and the Great Lakes region.')}
              </p>
            </div>
          </div>
          
          <div className="md:w-2/3">
            <div className="bg-white rounded-xl shadow-md p-8 border border-blue-100">
              <h4 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('programs.peace.details.title', 'Program Details')}
              </h4>
              <div className="space-y-6">
                {peaceDetails.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <span className="text-blue-600 font-semibold group-hover:text-white">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Target className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.peace.objectives.title', 'Program Objectives')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {objectives.map((objective, index) => (
            <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all border border-blue-100 group">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                <Award className="h-6 w-6 text-blue-600 group-hover:text-white" />
              </div>
              <p className="text-gray-700 leading-relaxed">{objective}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Users className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.peace.target.title', 'Target Groups')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl p-8 shadow-md border border-blue-100">
            <h3 className="text-2xl font-semibold text-blue-600 mb-6">
              {t('programs.peace.target.direct.title', 'Direct Beneficiaries')}
            </h3>
            <ul className="space-y-4">
              {targetGroups.direct.map((group, index) => (
                <li key={index} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <Clock className="h-5 w-5 text-blue-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-xl p-8 shadow-md border border-blue-100">
            <h3 className="text-2xl font-semibold text-blue-600 mb-6">
              {t('programs.peace.target.indirect.title', 'Indirect Beneficiaries')}
            </h3>
            <ul className="space-y-4">
              {targetGroups.indirect.map((group, index) => (
                <li key={index} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <Clock className="h-5 w-5 text-blue-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Selection Criteria Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Shield className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.peace.criteria.title', 'Selection Criteria')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {selectionCriteria.map((criterion, index) => (
            <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all border border-blue-100 group">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                <Award className="h-6 w-6 text-blue-600 group-hover:text-white" />
              </div>
              <p className="text-gray-700 leading-relaxed">{criterion}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Approaches Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Shield className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.peace.approaches.title', 'Our Approaches')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {approaches.map((approach, index) => (
            <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all border border-blue-100 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <div className="text-blue-600 group-hover:text-white">
                    {approach.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{approach.title}</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">{approach.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-xl p-12">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-6 text-white">
            {t('programs.peace.cta.title', 'Join Our Peace Building Efforts')}
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            {t('programs.peace.cta.description', 'Be part of our mission to build lasting peace in Rwanda and the Great Lakes region. Your involvement can make a difference.')}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/volunteer">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                {t('programs.peace.cta.volunteer', 'Volunteer With Us')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-blue-700 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                {t('programs.peace.cta.donate', 'Support Our Programs')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Peace;
